import { useGetShoppingList } from "@/hooks/requests/shopping-list/use-get-shopping-list";
import { useUpdateShoppingList } from "@/hooks/requests/shopping-list/use-update-shopping-list";
import { useSearchParams } from "@/hooks/router/use-search-params";
import { useToast } from "@/hooks/ui/use-toast";
import { useIonRouter } from "@ionic/react";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { groupIngredientsByAisle } from "../../utils/shopping-list-helpers";

export const useShopList = () => {
  const { ingredients, isLoading, error, refetchShoppingList } =
    useGetShoppingList();
  const { updateShoppingList, isUpdating } = useUpdateShoppingList();
  const searchParams = useSearchParams();
  const { showSuccess, showError } = useToast();
  const location = useLocation();
  const navigate = useIonRouter();

  const [groupedIngredients, ingredientCategories] = useMemo(() => {
    const groupedIngredients = groupIngredientsByAisle(ingredients ?? []);
    const ingredientCategories = Array.from(groupedIngredients.keys());
    return [groupedIngredients, ingredientCategories];
  }, [ingredients]);
  const selectedAisle = searchParams.get("aisle");
  const selectedIngredients = groupedIngredients.get(selectedAisle ?? "");

  const tickedItems = useMemo(() => {
    return new Set(
      ingredients
        ?.filter((ingredient) => ingredient.isTicked)
        .map((ingredient) => ingredient.ingredientId) ?? [],
    );
  }, [ingredients]);

  useEffect(() => {
    if (selectedAisle) return;
    if (groupedIngredients.size > 0) {
      const firstAisleWithItems = Array.from(groupedIngredients.keys()).find(
        (aisle) => (groupedIngredients.get(aisle)?.length || 0) > 0,
      );
      if (firstAisleWithItems) {
        searchParams.set("aisle", firstAisleWithItems);
        navigate.push(`${location.pathname}?${searchParams.toString()}`);
      }
    }
  }, [selectedAisle, groupedIngredients]);

  const handleToggleItem = useCallback(
    (ingredientId: string) => {
      return () => {
        const newTickedItems = new Set(tickedItems);

        if (newTickedItems.has(ingredientId)) {
          newTickedItems.delete(ingredientId);
        } else {
          newTickedItems.add(ingredientId);
        }

        const tickedIds = Array.from(newTickedItems);

        updateShoppingList(
          { tickedIngredientIds: tickedIds },
          {
            onError: (error) => {
              console.error("Failed to update shopping list:", error);
              showError("Failed to update shopping list.");
            },
          },
        );
      };
    },
    [tickedItems, updateShoppingList, showError],
  );

  const handleMarkAllAsBought = useCallback(() => {
    const allIngredientIds = ingredients?.map((ing) => ing.ingredientId) ?? [];

    updateShoppingList(
      { tickedIngredientIds: allIngredientIds },
      {
        onSuccess: () => {
          showSuccess("All items marked as bought!");
        },
        onError: (error) => {
          console.error("Failed to mark all as bought:", error);
          showError("Failed to mark all items as bought.");
        },
      },
    );
  }, [ingredients, updateShoppingList, showSuccess, showError]);

  const handleResetList = useCallback(() => {
    updateShoppingList(
      { tickedIngredientIds: [] },
      {
        onSuccess: () => {
          showSuccess("Shopping list reset!");
        },
        onError: (error) => {
          console.error("Failed to reset list:", error);
          showError("Failed to reset shopping list.");
        },
      },
    );
  }, [updateShoppingList, showSuccess, showError]);

  return {
    ingredients,
    isLoading,
    error,
    refetchShoppingList,
    selectedAisle,
    tickedItems,
    groupedIngredients,
    ingredientCategories,
    selectedIngredients,
    handleToggleItem,
    handleMarkAllAsBought,
    handleResetList,
    isUpdating,
  };
};
